import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Edit, Trash2 } from 'lucide-react';

/**
 * Unified Resource Card Component
 * Based on the clean Roles card design
 * Used for: Team, Assets, Roles
 */

const ResourceCard = ({
  // Data
  id,
  name,
  icon: Icon,
  iconColor = '#3B82F6',
  
  // Top-right badge (e.g. role name)
  topRightBadge, // { label, color }
  
  // Primary badge (shown next to name) — kept for back-compat
  primaryBadge,
  
  // Description/details text
  description,
  descriptionLines, // [{ icon: <ReactNode>, text: string }]
  
  // Details lines (for Team: email, phone; for Assets: capacity)
  details = [], // [{ icon, text }] or just array of strings
  
  // Expandable content (See More section)
  expandedContent,
  showSeeMore = false,
  isExpanded = false, // Controlled expansion state
  onToggleExpand, // Controlled expansion handler
  
  // Actions
  onEdit,
  onDelete,
  editLabel = 'Edit',
  deleteLabel = 'Delete',
  
  // Mass actions
  massActionMode = false,
  isSelected = false,
  onToggleSelect,
  
  // Left border colors (array for multi-color)
  leftBorderColors = [],
  
  // Additional elements
  additionalBadges, // JSX for extra badges (e.g., Pending)
  additionalClassNames = '' // For special cases like opacity, ring colors
}) => {
  const [internalExpanded, setInternalExpanded] = useState(false);
  
  // Use controlled state if provided, otherwise use internal state
  const expanded = onToggleExpand !== undefined ? isExpanded : internalExpanded;
  const toggleExpand = onToggleExpand || (() => setInternalExpanded(!internalExpanded));

  return (
    <Card 
      className={`hover:shadow-md transition-shadow relative overflow-hidden flex flex-col h-full ${isSelected ? 'ring-2 ring-blue-500' : ''} ${additionalClassNames}`}
      style={leftBorderColors.length > 0 ? {
        borderLeft: `4px solid ${leftBorderColors[0]}`
      } : {}}
    >
      {/* No absolute border div — border-l on the card gives natural rounded corners */}
      
      {/* Checkbox - top right */}
      {massActionMode && (
        <input
          type="checkbox"
          checked={isSelected}
          onChange={onToggleSelect}
          className="absolute top-3 right-3 w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer z-10"
        />
      )}
      
      <CardHeader className="p-4 pb-2 flex-shrink-0">
        <div className="flex items-center gap-2.5">
          {/* Icon */}
          <div 
            className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: `${iconColor}15`, border: `2px solid ${iconColor}` }}
          >
            <Icon className="w-4 h-4" style={{ color: iconColor }} />
          </div>
          {/* Name — truncates if too long */}
          <span className="text-sm font-semibold truncate flex-1 min-w-0">{name}</span>
          {/* Role badge — top right */}
          {topRightBadge && (
            <span
              className="text-[10px] font-medium px-1.5 py-0.5 rounded-full flex-shrink-0 max-w-[80px] truncate"
              style={{ backgroundColor: `${topRightBadge.color}15`, color: topRightBadge.color, border: `1px solid ${topRightBadge.color}40` }}
            >
              {topRightBadge.label}
            </span>
          )}
        </div>
        
        {/* Details (used by assets etc.) */}
        {details.length > 0 && (
          <div className="mt-2 space-y-0.5">
            {details.slice(0, 3).map((detail, idx) => (
              <div key={idx} className="flex items-center gap-1.5 text-xs text-gray-500 truncate">
                {typeof detail === 'string' ? <span className="truncate">{detail}</span> : (
                  <>
                    {detail.icon && <span className="flex-shrink-0 text-gray-400">{detail.icon}</span>}
                    <span className="truncate">{detail.text}</span>
                  </>
                )}
              </div>
            ))}
          </div>
        )}
        
        {additionalBadges && <div className="mt-1.5">{additionalBadges}</div>}
      </CardHeader>
      
      <CardContent className="px-0 pt-0 pb-0 flex flex-col flex-1">
        {/* Description — fixed 2-line height, supports icon+text lines or plain text */}
        {(description !== undefined || descriptionLines?.length > 0) && (
          <div className="px-4 pb-2 h-[2.75rem] overflow-hidden">
            {descriptionLines?.length > 0 ? (
              <div className="space-y-0.5">
                {descriptionLines.slice(0, 2).map((line, i) => (
                  <div key={i} className="flex items-center gap-1.5 text-xs text-gray-400 truncate">
                    {line.icon && <span className="flex-shrink-0 text-gray-300">{line.icon}</span>}
                    <span className="truncate">{line.text}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-xs text-gray-400 line-clamp-2 leading-snug whitespace-pre-line">
                {description || ''}
              </p>
            )}
          </div>
        )}

        {showSeeMore && expandedContent && (
          <div className="px-4 pb-2">
            <button onClick={toggleExpand} className="text-xs text-blue-600 hover:text-blue-800 font-medium">
              {expanded ? 'Less' : 'See More'}
            </button>
          </div>
        )}
        {expanded && expandedContent && (
          <div className="space-y-3 px-4 mb-3 pt-3 border-t">
            {expandedContent}
          </div>
        )}

        {/* Spacer — pushes Edit/Delete to bottom */}
        <div className="flex-1" />

        {/* Action Buttons — always flush at card bottom */}
        <div className="grid grid-cols-2 border-t border-gray-100 overflow-hidden">
          <button
            onClick={onEdit}
            className="flex items-center justify-center gap-1.5 py-2.5 text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-50 active:bg-gray-100 transition-colors outline-none focus:outline-none rounded-bl-2xl"
          >
            <Edit className="w-3.5 h-3.5" />
            {editLabel}
          </button>
          {onDelete ? (
            <button
              onClick={onDelete}
              className="flex items-center justify-center gap-1.5 py-2.5 text-sm font-medium text-red-500 hover:text-red-600 active:bg-red-50 transition-colors border-l border-gray-100 outline-none focus:outline-none rounded-br-2xl"
            >
              <Trash2 className="w-3.5 h-3.5" />
              {deleteLabel}
            </button>
          ) : (
            <div className="rounded-br-2xl" />
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default ResourceCard;
